<script module>
  export const i18n = {
    en: {
      YOUTH: 'youth',
      SENIOR: 'senior',
      ADULT: 'adult',
      '%s ADULT': {
        one: '%s adult',
        other: '%s adults'
      },
      '%s SENIOR': {
        one: '%s senior',
        other: '%s seniors'
      },
      '%s YOUTH': {
        one: '%s youth',
        other: '%s youths'
      },
      '%s people': {
        one: '%s person',
        other: '%s people'
      },
      'Spend %s nights': {
        one: 'Spend one night',
        other: 'Spend %s nights'
      }
    },
    sv: {
      YOUTH: 'ungdom',
      SENIOR: 'senior',
      ADULT: 'vuxen',
      '%s ADULT': {
        one: '%s vuxen',
        other: '%s vuxna'
      },
      '%s SENIOR': {
        one: '%s senior',
        other: '%s seniorer'
      },
      '%s YOUTH': {
        one: '%s ungdom',
        other: '%s ungdomar'
      },
      '%s people': {
        one: '%s person',
        other: '%s personer'
      },
      'Spend %s nights': {
        one: 'Stanna en natt',
        other: 'Stanna %s nätter'
      },
      'Edit search': 'Ändra sökning',
      Close: 'Stäng',
      Done: 'Klar',
      Search: 'Sök',
      'Leaving from': 'Från',
      'Going to': 'Till',
      'How many nights?': 'Hur många nätter?',
      'Add via': 'Lägg till via',
      'Change via': 'Ändra via',
      Cancel: 'Avbryt',
      Remove: 'Ta bort',
      'Passing through': 'Genomresa',
      'Other places in %s': 'Andra platser i %s',
      'Popular places': 'Populära platser',
      'age at departure': 'ålder vid avresa',
      'Please input the %s for each passenger to ensure that you are offered the best available price.':
        'Ange %s för varje passagerare för att säkerställa att du erbjuds det bästa tillgängliga priset.'
    }
  }
</script>

<script>
  import { parse, toDays, toString as toDuration } from 'duration-fns'
  import { tick } from 'svelte'

  import Datepicker, { parseDate } from '$lib/Datepicker.svelte'
  import { getPassengersLabel } from '$lib/utils/passengers.js'
  import { i18n as countryNames } from '$lib/countries.js'
  import LocationSearch from './LocationSearch.svelte'
  import { register } from '$lib/utils/text.svelte.js'
  import { preload } from '$lib/Autocomplete.svelte'
  import Passengers from '$lib/Passengers.svelte'
  import State from '$lib/utils/state.svelte.js'
  import { capture } from '$lib/capture.js'
  import Counter from '$lib/Counter.svelte'
  import Dropdown from './Dropdown.svelte'
  import Symbol from '$lib/Symbol.svelte'
  import Button from '$lib/Button.svelte'
  import Image from '$lib/Image.svelte'

  let text = register(i18n, countryNames)

  /**
   * @type {{
   *   origin?: Schema.Location?,
   *   destination?: Schema.Location?,
   *   via?: Schema.Location?,
   *   duration?: string,
   *   departureDate: string,
   *   passengers: Schema.PassengerPlaceholderInput[],
   *   panel?: 'origin' | 'via' | 'destination' | 'ddate' | 'passengers' | null
   * }}
   */
  let {
    origin = $bindable(),
    destination = $bindable(),
    via = $bindable(),
    duration = $bindable(),
    departureDate = $bindable(),
    passengers = $bindable(),
    panel
  } = $props()

  let stopover = $derived(duration ? parse(duration) : undefined)
  let passengerLabel = $derived(getPassengersLabel(passengers))
  let date = $derived(parseDate(departureDate))

  let disabled = $derived(
    [origin?.uid, via?.uid, destination?.uid]
      .filter(Boolean)
      .some((uid, index, list) => index && list[index - 1] === uid)
  )

  let isAnimating = $state(false)
  let isExpanded = $state(Boolean(panel))

  let summary
  let container
  let background
  const cells = {}

  const toggle = async (isExpanding = !isExpanded) => {
    if (isExpanding) {
      isExpanded = true
      await tick()
    }

    const from = isExpanding ? summary.clientHeight : container.clientHeight
    const to = isExpanding ? container.clientHeight : summary.clientHeight
    const target = isExpanding ? container : background

    if (!isExpanding) isExpanded = false
    isAnimating = true

    const animation = target.animate(
      [{ height: `${from}px` }, { height: `${to}px` }],
      {
        duration: 450,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
      }
    )

    await animation.finished
    isAnimating = false
  }

  const select = (value) => {
    panel = value
  }

  const onclick = (event) => {
    if (!panel) return
    let parent = event.target
    if (parent.name === 'panel') return
    while (parent) {
      if (parent.isSameNode(cells[panel])) return
      parent = parent.parentElement
    }
    panel = null
  }
</script>

<svelte:document on:click|capture={onclick} />

<div class="journey-search-bar" class:animating={isAnimating}>
  <input
    id="journey-search-bar-toggle"
    type="checkbox"
    class="state u-hidden"
    checked={isExpanded} />

  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
  <label
    class="toggle"
    for="journey-search-bar-toggle"
    onclick={(event) => {
      toggle()
      event.preventDefault()
    }}>
    <span class="open">{text`Edit search`}</span>
    <span class="close">{text`Close`}</span>
  </label>

  <div class="background" bind:this={background}></div>

  <div class="container summary" bind:this={summary}>
    <span class="locations">
      <strong>{origin?.name || text`Leaving from`}</strong>
      <Symbol name="arrow" size="sm" />
      {#if via}
        <strong>{via.name}</strong>
        <Symbol name="arrow" size="sm" />
      {/if}
      <strong>{destination?.name || text`Going to`}</strong>
    </span>
    <span>
      <Symbol name="calendar" prefix size="sm" />
      {date.toLocaleDateString(State.language, {
        day: 'numeric',
        month: 'short'
      })}
    </span>
    <span>
      <Symbol name="person" prefix size="sm" />
      {text(`%s ${passengerLabel}`, passengers.length)}
    </span>
  </div>

  <div class="container {panel || ''}" class:panel bind:this={container}>
    <div class="locations" class:panel class:via class:control={via}>
      <div class="cell origin" bind:this={cells.origin}>
        {#if panel === 'origin'}
          <div class:control={!via} class="fill">
            <LocationSearch
              align="left"
              label={text`Leaving from`}
              name="origin"
              fallbackCountryCode={origin?.countryCode}
              onclose={() => select(null)}
              onchange={(location) => {
                origin = location
                select(null)
              }} />
          </div>
        {:else}
          <button
            name="panel"
            value="origin"
            class="location"
            class:control={!via}
            onmouseenter={() => preload(origin?.countryCode)}
            ontouchstart={() => preload(origin?.countryCode)}
            onclick={(event) => {
              select('origin')
              event.preventDefault()
            }}>
            <div class="thumbnail">
              {#if origin?.image}
                <Image
                  src={origin.image.url}
                  alt={origin.image.alt || origin.name}
                  sizes="(width >= 1000px) 1.5rem, 2.25rem"
                  width={origin.image.width}
                  height={origin.image.height}
                  aspect={1}
                  transforms="q_70"
                  variants={[36, 72]}
                  class="img" />
              {:else}
                <Symbol name="pin" size="sm" />
              {/if}
            </div>
            {#if origin}
              <span class="truncate">
                <span class="label">
                  {text`Leaving from`}
                  <br />
                </span>
                {origin.name}
                <span class="normal">{text(origin.countryCode)}</span>
              </span>
            {:else}
              {text`Leaving from`}
            {/if}
          </button>
        {/if}
      </div>

      <div class="cell divider">
        <Symbol name="arrow" size="sm" />
      </div>

      <div class="cell via" bind:this={cells.via}>
        {#if panel === 'via'}
          <div class:control={!via} class="fill">
            <LocationSearch
              align="left"
              label={text`Via`}
              name="via"
              fallbackCountryCode={via?.countryCode}
              onclose={() => select(null)}
              onchange={(location) => {
                via = location
                select(null)
              }}>
              <fieldset class="stopover">
                <div class="thumbnail large">
                  {#if via?.image}
                    <Image
                      src={via.image.url}
                      alt={via.image.alt || via.name}
                      sizes="(width >= 1000px) 2.5rem, 2rem"
                      width={via.image.width}
                      height={via.image.height}
                      aspect={1}
                      transforms="q_70"
                      variants={[64, 80]}
                      class="img" />
                  {:else}
                    <Symbol name="pin" />
                  {/if}
                </div>
                <div class="truncate">
                  {#if via}
                    {via.name}
                    <span class="normal">{text(via.countryCode)}</span>
                  {:else}
                    <strong>{text`How many nights?`}</strong>
                  {/if}
                  <br />
                  <span class="normal">
                    {stopover?.days
                      ? text`Spend ${toDays(stopover)} nights`
                      : text`Passing through`}
                  </span>
                </div>
                <div class="days">
                  <Counter
                    size="sm"
                    name="duration"
                    value={stopover ? Math.floor(toDays(stopover)) : 0}
                    onchange={(days) => (duration = toDuration({ days }))} />
                </div>
                <div class="remove">
                  <Button
                    collapse
                    title={text`Remove`}
                    onclick={(event) => {
                      panel = null
                      via = undefined
                      event.preventDefault()
                    }}>
                    <Symbol name="cross" size="xs" />
                    <span class="u-hidden">{text`Remove`}</span>
                  </Button>
                </div>
              </fieldset>
            </LocationSearch>
          </div>
        {:else}
          <button
            name="panel"
            value="via"
            class="location via"
            class:add={!via}
            title={via ? text`Change via` : text`Add via`}
            onmouseenter={() => preload(via?.countryCode)}
            ontouchstart={() => preload(via?.countryCode)}
            onclick={(event) => {
              select('via')
              event.preventDefault()
            }}>
            {#if via}
              <div class="thumbnail">
                {#if via.image}
                  <Image
                    src={via.image.url}
                    alt={via.image.alt || via.name}
                    sizes="(width >= 1000px) 1.5rem, 2.25rem"
                    width={via.image.width}
                    height={via.image.height}
                    aspect={1}
                    transforms="q_70"
                    variants={[36, 72]}
                    class="img" />
                {:else}
                  <Symbol name="pin" size="sm" />
                {/if}
              </div>
              <span class="truncate">
                <span class="label">
                  {text`Via`}
                  <br />
                </span>
                {via.name}
                <span class="normal">{text(via.countryCode)}</span>
              </span>
            {:else}
              <span class="plus">
                <Symbol name="plus" size="sm" />
              </span>
            {/if}
          </button>
        {/if}
      </div>

      <div class="cell divider">
        <Symbol name="arrow" size="sm" />
      </div>

      <div class="cell destination" bind:this={cells.destination}>
        {#if panel === 'destination'}
          <div class:control={!via} class="fill">
            <LocationSearch
              align="left"
              label={text`Going to`}
              name="destination"
              fallbackCountryCode={destination?.countryCode}
              onclose={() => select(null)}
              onchange={(location) => {
                destination = location
                select(null)
              }} />
          </div>
        {:else}
          <button
            name="panel"
            value="destination"
            class="location"
            class:control={!via}
            onmouseenter={() => preload(destination?.countryCode)}
            ontouchstart={() => preload(destination?.countryCode)}
            onclick={(event) => {
              select('destination')
              event.preventDefault()
            }}>
            <div class="thumbnail">
              {#if destination?.image}
                <Image
                  src={destination.image.url}
                  alt={destination.image.alt || destination.name}
                  width={destination.image.width}
                  height={destination.image.height}
                  sizes="(width >= 1000px) 1.5rem, 2.25rem"
                  aspect={1}
                  transforms="q_70"
                  variants={[36, 72]}
                  class="img" />
              {:else}
                <Symbol name="pin" size="sm" />
              {/if}
            </div>
            {#if destination}
              <span class="truncate">
                <span class="label">
                  {text`Going to`}
                  <br />
                </span>
                {destination.name}
                <span class="normal">{text(destination.countryCode)}</span>
              </span>
            {:else}
              {text`Going to`}
            {/if}
          </button>
        {/if}
      </div>
    </div>

    <div class="cell ddate" bind:this={cells.ddate}>
      {#if panel === 'ddate'}
        <fieldset class="capture" use:capture>
          <div class="control ddate">
            <Symbol name="calendar" />
            <span class="text">
              {date.toLocaleDateString(State.language, {
                day: 'numeric',
                month: 'short'
              })}
            </span>
            <div class="actions">
              <Button
                size="xs"
                name="panel"
                value=""
                title={text`Cancel`}
                onclick={(event) => {
                  select(null)
                  event.preventDefault()
                }}>
                {text`Done`}
              </Button>
            </div>
          </div>
          <Dropdown align="right">
            <Datepicker
              name="ddate"
              bind:value={departureDate}
              on:change={(event) => {
                departureDate = event.detail
                select(null)
              }} />
          </Dropdown>
        </fieldset>
      {:else}
        <button
          name="panel"
          value="ddate"
          class="control ddate"
          onclick={(event) => {
            select('ddate')
            event.preventDefault()
          }}>
          <Symbol name="calendar" />
          {date.toLocaleDateString(State.language, {
            day: 'numeric',
            month: 'short'
          })}
        </button>
      {/if}
    </div>

    <div class="cell passengers" bind:this={cells.passengers}>
      {#if panel === 'passengers'}
        <fieldset class="capture" use:capture>
          <div class="control passengers">
            <Symbol name="person" />
            <span class="text">
              {text(`%s ${passengerLabel}`, passengers.length)}
            </span>
            <div class="actions">
              <noscript>
                <Button size="xs" type="submit">{text`Save`}</Button>
              </noscript>
              <Button
                size="xs"
                name="panel"
                value=""
                title={text`Cancel`}
                onclick={(event) => {
                  select(null)
                  event.preventDefault()
                }}>
                {text`Done`}
              </Button>
            </div>
          </div>
          <Dropdown align="right">
            <Passengers bind:passengers />
            <div class="footer">
              <p>
                {@html text`Please input the ${`<strong>${text`age at departure`}</strong>`} for each passenger to ensure that you are offered the best available price.`}
              </p>
            </div>
          </Dropdown>
        </fieldset>
      {:else}
        <button
          name="panel"
          value="passengers"
          class="control passengers"
          onclick={(event) => {
            select('passengers')
            event.preventDefault()
          }}>
          <Symbol name="person" />
          <span class="short">{passengers.length}</span>
          <span class="long">
            {text(`%s ${passengerLabel}`, passengers.length)}
          </span>
        </button>
      {/if}
    </div>

    <div class="cell submit">
      <button
        {disabled}
        type="submit"
        class="control submit"
        onclick={() => toggle(false)}>
        <span class="long">
          <Symbol name="magnifier" />
        </span>
        {text`Search`}
      </button>
    </div>
  </div>
</div>

<style>
  .journey-search-bar {
    display: flex;
    position: relative;
    z-index: 2;
    white-space: nowrap;
    user-select: none;
  }

  @media (width < 1000px) {
    .journey-search-bar {
      border-top: 1px solid rgba(var(--rgb-gray), 0.25);
    }
  }

  .background {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;
    background: var(--background);
  }

  .container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.25rem 1.2rem;
    width: 100%;
    padding: clamp(0.5rem, min(2vh, 3vw), 1rem) var(--document-margin);
    background: var(--background);
  }

  .journey-search-bar.animating .container {
    overflow: hidden;
  }

  .container.summary {
    font-size: 1rem;
    color: rgb(var(--rgb-muted));
  }

  @media (width < 1000px) {
    .state:not(:checked) ~ .container:not(.summary) {
      display: none;
    }

    .state:checked ~ .container:not(.summary) {
      position: absolute;
      gap: 1rem;
    }

    .state:checked ~ .container.panel {
      height: 100%;
    }
  }

  @media (width >= 1000px) {
    .container.summary {
      display: none;
    }

    .container {
      flex-direction: row;
      flex-wrap: nowrap;
      gap: 1rem;
      padding-top: 0;
      margin: 0 auto;
      width: min(
        calc(100vw - var(--document-margin) * 2),
        var(--container-large-width)
      );
    }

    @supports (padding-left: env(safe-area-inset-left)) {
      .container {
        padding-left: env(safe-area-inset-left);
        padding-right: env(safe-area-inset-right);
      }
    }
  }

  /** Toggle */

  .toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    font-weight: bold;
    color: rgb(var(--rgb-white));
    overflow: hidden;
    text-indent: -200vw;
  }

  .state:checked:focus-visible ~ .toggle {
    text-indent: 0;
  }

  .toggle::after {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    top: 1px;
    left: 0;
    z-index: -1;
    background: rgba(31, 31, 34, 0);
    transition: background-color 500ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .state:focus-visible ~ .toggle {
    border: solid var(--current-focus-ring-width, var(--focus-ring-width));
    border-color: rgb(var(--current-focus-ring-color, var(--focus-ring-color)));
  }

  @media (width >= 1000px) {
    .toggle {
      display: none;
    }
  }

  @media (width < 1000px) {
    .state:checked + .toggle {
      width: 100vw;
      height: 100vh;
      position: absolute;
      inset: 0;
      z-index: -1;
    }

    .state:checked + .toggle::after {
      background: rgba(31, 31, 34, 0.65);
    }

    :root:has(.state:checked) {
      overflow: hidden;
    }
  }

  .state:checked ~ .toggle .open {
    display: none;
  }

  .state:not(:checked) ~ .toggle .close {
    display: none;
  }

  /** Cell */

  .cell {
    display: flex;
    position: relative;
    min-width: 0;
  }

  @media (width >= 1000px) {
    .cell {
      align-items: center;
    }
  }

  @media (width < 1000px) {
    .locations .cell {
      width: 100%;
    }

    .container.panel .cell {
      width: 100%;
    }

    .container.panel:not(.origin) .cell.origin,
    .container.panel:not(.via) .cell.via,
    .container.panel:not(.destination) .cell.destination,
    .container.panel:not(.origin, .via, .destination) .locations,
    .container.panel:not(.ddate) .cell.ddate,
    .container.panel:not(.passengers) .cell.passengers,
    .container.panel:not(.submit) .cell.submit {
      display: none;
    }
  }

  .capture {
    width: 100%;
  }

  /** Controls */

  .control {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    font-weight: bold;
    line-height: 1;
    word-spacing: 0;
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    border-radius: var(--border-radius-full);
    box-shadow: var(--box-shadow-sm);
    transition: var(--box-shadow-transition);
  }

  .fill {
    display: flex;
    width: 100%;
  }

  .control:hover {
    box-shadow: var(--box-shadow-sm-hover);
  }

  .control:active {
    box-shadow: var(--box-shadow-sm-active);
  }

  @media (width < 1000px) {
    .control.locations:not(.panel) {
      padding: 0;
    }

    .control.location {
      border-radius: 0;
    }

    .container.panel .control {
      width: 100%;
    }
  }

  @media (width >= 1000px) {
    .control {
      padding: 0.8rem 1rem;
    }

    .locations.control {
      padding: 0 1rem 0 0.5rem;
    }
  }

  .long {
    display: none;
  }

  @media (width >= 1000px) {
    .short {
      display: none;
    }

    .long {
      display: initial;
    }
  }

  .text {
    margin-right: auto;
  }

  .actions {
    display: flex;
    gap: 0.5rem;
  }

  .actions > :global(*) {
    display: flex;
  }

  @media (width >= 1000px) {
    .control .actions {
      clip: rect(1px, 1px, 1px, 1px);
      height: 1px;
      overflow: hidden;
      position: absolute;
      width: 1px;
    }
  }

  /** Locations */

  .locations {
    flex: 1 99 auto;
    display: flex;
    align-items: stretch;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    min-width: 0;
    font-size: 1rem;
    color: rgb(var(--rgb-text));
  }

  @media (width > 1000px) {
    .locations {
      display: grid;
      grid-template-columns: 1fr auto 1fr;
    }

    .locations.via {
      grid-template-columns: 1fr auto 1fr auto 1fr;
    }
  }

  .container.summary .locations {
    align-items: center;
  }

  @media (width < 1000px) {
    .locations.via:not(.control) {
      padding: 0;
    }

    .state:checked ~ .container:not(.summary, .panel) .locations {
      flex-direction: column;
      gap: 0;
      border: 1px solid rgba(var(--rgb-gray), 0.25);
      border-radius: var(--border-radius-md);
      box-shadow: 0 0.125rem 0.25rem 0 rgba(var(--rgb-blue), 0.03);
    }
  }

  .location {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    gap: 0.625rem;
    min-width: 0;
    overflow: hidden;
    font-weight: 600;
    line-height: 1.2;
  }

  @media (width < 1000px) {
    .location {
      width: 100%;
    }

    .state:checked ~ .container:not(.summary) .location {
      padding: 0.5rem 0.75rem;
      border: 0;
      box-shadow: none;
    }
  }

  @media (width >= 1000px) {
    .control.location,
    .control.location {
      flex: 1 1 auto;
    }
  }

  @media (width >= 1000px) {
    .locations.via .location {
      padding: 0.5rem 0.75rem;
      height: 100%;
    }
  }

  .thumbnail {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    color: rgb(var(--rgb-text));
    position: relative;
    width: 2.25rem;
    height: 2.25rem;
    overflow: hidden;
    border-radius: var(--border-radius-xs);
    background-color: rgb(var(--rgb-bright-gray));
  }

  .thumbnail.large {
    width: 2rem;
    height: 2rem;
  }

  @media (min-width: 1000px) {
    .thumbnail {
      width: 1.5rem;
      height: 1.5rem;
    }

    .thumbnail.large {
      width: 2.5rem;
      height: 2.5rem;
    }
  }

  .thumbnail :global(.img) {
    object-fit: cover;
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
  }

  .truncate {
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    transform: translateY(0.075em); /* Compensate for line height */
  }

  .label {
    font-size: 0.875rem;
    font-weight: normal;
    color: rgb(var(--rgb-muted));
  }

  @media (width >= 1000px) {
    .label {
      display: none;
    }
  }

  .normal {
    font-weight: normal;
  }

  .divider {
    display: none;
  }

  @media (width >= 1000px) {
    .locations.via .divider {
      display: initial;
      align-self: center;
    }
  }

  @media (width < 1000px) {
    .location.via {
      display: flex;
      align-items: center;
      font-weight: bold;
      padding: 0.25rem 0.75rem;
    }

    .locations:not(.via) .location.via::before {
      content: '';
      flex: 1 1 auto;
      order: 1;
      transform: translateY(1px);
      border-bottom: 1px solid rgba(var(--rgb-gray), 0.25);
    }

    .locations:not(.via) .location.via::after {
      content: attr(title);
    }
  }

  .plus {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 100%;
    background-color: rgb(var(--rgb-bright-gray));
  }

  .stopover {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
    margin-bottom: 1.5rem;
    position: relative;
    line-height: 1.2;
  }

  .days {
    margin-left: auto;
  }

  .remove {
    position: absolute;
    left: -0.5rem;
    top: -0.5rem;
  }

  /** Passengers */

  .footer {
    margin-top: 1.5rem;
    white-space: normal;
    font-size: 0.875rem;
    color: rgb(var(--rgb-muted));
  }

  /** Submit */

  .cell.submit {
    margin-left: auto;
  }

  .control.submit {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
    border: 0;
    color: rgb(var(--rgb-white));
    background: rgb(var(--rgb-highlight));
    border: 1px solid transparent;
  }

  .control.submit:disabled {
    background: var(--background-alt);
    color: rgb(var(--rgb-muted));
    border: 1px solid rgba(var(--rgb-gray), 0.25);
  }

  @media (width < 1000px) {
    .state:not(:checked) ~ .container .control.submit {
      display: none;
    }
  }

  @media (width >= 1000px) {
    .control.submit {
      padding-left: 1.5rem;
      padding-right: 2rem;
    }
  }
</style>
